import api from './client'

/**
 * Голоса персонажей «Сцены» — каталог голосов ElevenLabs (бэк — services/elevenlabs_voices, ручка
 * GET /api/v1/voice/catalog). Голос выбирают персонажу на его странице (PUT /assets/{id}/voice, api/assets.ts), а
 * говорит он в видео «Сцены». Открыто тем, кому открыта «Сцена» (useSceneOpen).
 */

export type VoiceGender = 'm' | 'f'
export type VoiceAge = 'young' | 'adult' | 'old'
export type VoiceLanguage = 'ru' | 'all'
/** Акцент голоса (метка библиотеки ElevenLabs) — фильтр «Все голоса», выбирает человек. */
export type VoiceAccent = 'american' | 'british' | 'australian' | 'indian' | 'african'

/** Карточка голоса — одна форма для каталога и для голоса, уже выбранного персонажу. */
export interface VoiceCard {
	id: string
	/** Имя голоса без описания («Dmitry»). */
	name: string
	gender: VoiceGender | null
	age: VoiceAge | null
	/** Акцент по меткам ElevenLabs (английским словом), null — не указан. */
	accent: string | null
	/** Язык голоса (код ISO), null — не указан. */
	language: string | null
	/** Родной русский голос или проверенный ElevenLabs на русском. */
	russian: boolean
	/** Короткая подпись по-русски («зрелый · для рассказов»). */
	hint: string | null
	/** Проба голоса — русская, если она есть. */
	preview_url: string | null
	/** library — библиотека голосов ElevenLabs, default — стандартные голоса. */
	source: 'library' | 'default'
}

export interface VoiceFilters {
	gender: VoiceGender | null
	age: VoiceAge | null
	language: VoiceLanguage
	search: string
	/** Только вместе с «Все языки»: у русских голосов акцент один. */
	accent?: VoiceAccent | null
}

export interface VoiceCatalogPage {
	ok: boolean
	voices: VoiceCard[]
	has_more: boolean
	/** Понятная подпись, если библиотека или все голоса сейчас недоступны. */
	error: string | null
}

export const DEFAULT_VOICE_FILTERS: VoiceFilters = { gender: null, age: null, language: 'ru', search: '' }

/** Фильтры → строка запроса каталога (пустые не отправляем). */
export function voiceCatalogQuery(filters: VoiceFilters, page: number): string {
	const q = new URLSearchParams()
	if (filters.gender) q.set('gender', filters.gender)
	if (filters.age) q.set('age', filters.age)
	q.set('language', filters.language)
	if (filters.accent && filters.language === 'all') q.set('accent', filters.accent)
	const search = filters.search.trim().replace(/\s+/g, ' ').slice(0, 60)
	if (search) q.set('search', search)
	if (page > 0) q.set('page', String(page))
	return q.toString()
}

export async function getVoiceCatalog(filters: VoiceFilters, page = 0): Promise<VoiceCatalogPage> {
	return api.request<VoiceCatalogPage>(`/api/v1/voice/catalog?${voiceCatalogQuery(filters, page)}`)
}

/** Проба голоса одной фразой (фраза — из админки, одна на все голоса, чтобы сравнивать голоса на одинаковой речи).
 *  url — файл пробы этим голосом (синтезируется при первом запросе, дальше из хранилища); null — фраза не задана или
 *  синтез не удался: тогда играем пробу ElevenLabs (preview_url). */
export interface VoiceProbe {
	ok: boolean
	url: string | null
	phrase: string
	source: 'phrase' | 'sample' | null
	error?: string | null
}

/** characterId — чьей фразой озвучить: своя фраза персонажа важнее общей. */
export async function getVoiceProbe(voiceId: string, characterId?: string | null): Promise<VoiceProbe> {
	const q = characterId ? `?character_id=${encodeURIComponent(characterId)}` : ''
	return api.request<VoiceProbe>(`/api/v1/voice/probe/${encodeURIComponent(voiceId)}${q}`)
}
